import { useForm, Controller } from "react-hook-form"
import { string, z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { validateVAT } from "../../VATField";


import { 
  TextField, FormControlLabel, Checkbox, 
  FormHelperText, Grid, Box, Link, 
  styled,
  Stack
} from "@mui/material"
import { 
  Button, RichHTML
} from "@gisce/oficina-virtual-components";
import { useTranslation } from "react-i18next";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { useNavigate } from "react-router-dom";
import { SignUpData } from "@/components/SignUp/types/signup";
import { useSignUp } from "../hooks/useSignUp";
import { useEffect } from "react";
import { ArrowForward } from '@mui/icons-material';
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
.MuiTelInput-IconButton {
  display: none;
}
`;

const SignUpForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Settings = useSettingsContext();
  const {
    errors: backendErrors,
    state: formState,
    confirmForm,
    removeError: removeBackendFieldError
  } = useSignUp();

  const schema = z.object({
    contract: z.string().min(1, t('common:text.required_field')),
    nif: z.string().min(1, t('common:text.required_field'))
      .refine(
        nif => validateVAT(nif), 
        {message: t('common:text.vat_field_validation_invalid')}
      ),
    email: z.string()
      .min(1, t('common:text.required_field'))
      .email({ message: t('common:text.contractation_email_not_valid') }),
    email2: z.string()
      .min(1, t('common:text.required_field'))
      .email({ message: t('common:text.contractation_email_not_valid') }),
    ...(Settings.signUp?.phoneField &&
      {
        phone: z.string()
        .min(4, t('common:text.required_field'))
        .refine(
          (tel) => matchIsValidTel(tel), 
          {message: t('common:text.lead_contact_phone_not_valid')}
        ),
      }
    ),
    ...(Settings.signUp?.invoiceNumberField && 
      {
        invoiceNumber: z.string().min(1, t('common:text.required_field'))
      }
    ),
    acceptConditionsAndGdpr: z.boolean().refine((val) => val === true, {
      message: t('common:text.required_field'),
    }),
  }).superRefine((data, ctx) => {
    if (data.email !== data.email2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Els correus no coincideixen',
        path: ["email2"],
      });
    }
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch
  } = useForm<SignUpData>({
    resolver: zodResolver(schema),
    defaultValues: {
      acceptConditionsAndGdpr: false
    }
  });

  useEffect(() => {
    if (backendErrors?.nif) {
      removeBackendFieldError("nif");
    }
  }, [watch("nif")]); 

  useEffect(() => {
    if (backendErrors?.contract) {
      removeBackendFieldError("contract");
    }
  }, [watch("contract")]); 

  useEffect(() => {
    if (backendErrors?.email) {
      removeBackendFieldError("email");
    }
  }, [watch("email")]); 

  useEffect(() => {
    if (backendErrors?.email2) {
      removeBackendFieldError("email2");
    }
  }, [watch("email2")]);

  useEffect(() => {
    if (backendErrors?.accept_conditions) {
      removeBackendFieldError("accept_conditions");
    }

    if (backendErrors?.accept_gdpr) {
      removeBackendFieldError("accept_gdpr");
    }
  }, [watch("acceptConditionsAndGdpr")]);

  const onSignUpFormSubmit = (values: SignUpData) => {
    values.nif = values.nif.replace('ES', '');

    const data = {
      nif: values.nif,
      contract: values.contract,
      email: values.email,
      email2: values.email2,
      accept_conditions: values.acceptConditionsAndGdpr,
      accept_gdpr: values.acceptConditionsAndGdpr,
      ...(Settings.signUp?.phoneField && {phone: values.phone}),
      ...(Settings.signUp?.invoiceNumberField && {invoiceNumber: values.invoiceNumber})
    };

    confirmForm(data);
  };

  return formState != "submitted" ? (
    <Box sx={{p: {xs: 0, sm:2}}}>
      <form onSubmit={handleSubmit(onSignUpFormSubmit)}>
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <TextField
              label={t('common:signup.nif.label')}
              {...register("nif")}
              error={!!(errors.nif) || !!(backendErrors?.nif)}
              style={{ width: "100%" }}
              disabled={formState == "submitting"}
            />
            {!!(errors.nif?.message) &&
              <FormHelperText error={true}>
                {errors.nif.message}
              </FormHelperText>
            }
            {!!(backendErrors?.nif) &&
              <FormHelperText error={true}>
                {t(backendErrors.nif)}
              </FormHelperText>
            }
          </Grid>
        
          <Grid item xs={12}>
            <TextField
              label={t('common:signup.contract.label')}
              {...register("contract")}
              error={!!(errors.contract) || !!(backendErrors?.contract)}
              style={{ width: "100%" }}
              disabled={formState == "submitting"}
            />
            {!!(errors.contract?.message) &&
              <FormHelperText error={true}>
                {errors.contract.message}
              </FormHelperText>
            }
            {!!(backendErrors?.contract) &&
              <FormHelperText error={true}>
                {t(backendErrors.contract)}
              </FormHelperText>
            }
          </Grid>

          {Settings.signUp?.invoiceNumberField && (
            <Grid item xs={12}>
              <TextField
                label={t('common:signup.invoice.label')}
                {...register("invoiceNumber")}
                error={!!(errors.invoiceNumber)}
                style={{ width: "100%" }}
                disabled={formState == "submitting"}
              />
              {!!(errors.invoiceNumber?.message) &&
                <FormHelperText error={true}>
                  {errors.invoiceNumber.message}
                </FormHelperText>
              }
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label={t('common:signup.email.label')}
              {...register("email")}
              error={!!(errors.email) || !!(backendErrors?.email)}
              style={{ width: "100%" }}
              disabled={formState == "submitting"}
            />
            {!!(errors.email?.message) &&
              <FormHelperText error={true}>
                {errors.email.message}
              </FormHelperText>
            }
            {!!(backendErrors?.email) &&
              <FormHelperText error={true}>
                {t(backendErrors.email)}
              </FormHelperText>
            }
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('common:signup.email2.label')}
              {...register("email2")}
              error={!!(errors.email2) || !!(backendErrors?.email2)}
              style={{ width: "100%" }}
              disabled={formState == "submitting"}
            />
            {!!(errors.email2?.message) &&
              <FormHelperText error={true}>
                {errors.email2.message}
              </FormHelperText>
            }
            {!!(backendErrors?.email2) &&
              <FormHelperText error={true}>
                {t(backendErrors.email2)}
              </FormHelperText>
            }
          </Grid>

          {Settings.signUp?.phoneField && (
            <Grid item xs={12}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <NoFlagMuiTelInputStyled
                    inputProps={{ maxLength: 12 }}
                    disableDropdown={true}
                    label={t('common:signup.phone.label')}
                    forceCallingCode={true}
                    defaultCountry="ES"
                    helperText={errors.phone?.message}
                    fullWidth={true}
                    error={!!errors.phone?.message}
                    {...field}
                    disabled={formState == "submitting"}
                  />
                )}
              />
            </Grid>
          )}
        
          <Grid item xs={12}>
            <Controller
              name="acceptConditionsAndGdpr"
              control={control}
              render={({field}) => <FormControlLabel
                control={<Checkbox 
                  {...field}
                  checked={field.value}
                  disabled={formState == "submitting"}
                  />} 
                label={
                  <RichHTML html={t("common:text.contractation_ov_gdpr_accept", {
                    linkOV: Settings.links?.conditions ?? '',
                    linkGDPR: Settings.links?.gdpr ?? ''
                  })}
                  />
                } 
              />}
            />
            {!!(errors.acceptConditionsAndGdpr?.message) &&
              <FormHelperText error={true}>
                {errors.acceptConditionsAndGdpr.message}
              </FormHelperText>
            }
            {!!(backendErrors?.accept_conditions || backendErrors?.accept_gdpr) &&
              <FormHelperText error={true}>
                {t("common:signup.conditions.required")}
              </FormHelperText>
            }
          </Grid>
      
          <Grid item xs={12}>
            <Button
              color={'primary'}
              variant={'contained'}
              type="submit"
              fullWidth
              loading={formState == "submitting"}
            >
              {t('common:signup.button_activate')}
            </Button>
          </Grid>

          <Grid item xs={12} sx={{mt: 3}} justifyContent="center" display="flex">
            {t('common:signup.info_already_registered')}
            <Link 
              onClick={() => navigate(t('common:url.login'))} 
              underline="hover" 
              sx={{ml: 2}} 
              href="#"
            >
              {t('common:text.login_view_button_send')}
            </Link>
          </Grid>
        
        </Grid>
      </form>
    </Box>
  ) : (formState == "submitted" && <>
      <Stack direction="column" mt={2} gap={2}>
        <div>
          {t("common:signup.success_body")} <br/>
          {t("common:signup.success_body_password_info")}
        </div>
        <Stack
            justifyContent="center"
            direction="row"
        >
          <Button 
            color="primary"
            variant="contained"
            endIcon={<ArrowForward/>}
            onClick={() => navigate(t('common:url.login'))} 
          >
            {t("common:text.go_home")}
          </Button>
        </Stack>
      </Stack>
    </>    
  );
}

export default SignUpForm;